import type {
  IBaseAttributes,
  IListOptions,
  IListParams,
  ListResponse,
  TConstraints,
} from '@feathr/rachis';
import { Collection, Model } from '@feathr/rachis';

/** Longest a callout's text may be, per the Google Ads v22 CalloutAsset proto. */
export const MAX_CALLOUT_TEXT = 25;

export interface IGoogleAdsCallout extends IBaseAttributes {
  readonly customer: string;
  callout_text: string;
  readonly is_archived: boolean;
  readonly resource_name: string | null;
}

/** A callout-asset row shown alongside Google Ad Grants Search ads for one customer. */
export class GoogleAdsCallout extends Model<IGoogleAdsCallout> {
  public readonly className = 'GoogleAdsCallout';

  public get constraints(): TConstraints<IGoogleAdsCallout> {
    return {
      callout_text: {
        // validate.js treats a whitespace-only string as empty, so this alone also
        // rejects '   ' without a separate check.
        presence: { allowEmpty: false, message: '^Callout text is required.' },
        length: {
          maximum: MAX_CALLOUT_TEXT,
          tooLong: `^Callout text must be ${MAX_CALLOUT_TEXT} characters or fewer.`,
        },
      },
    };
  }

  public override getDefaults(): Partial<IGoogleAdsCallout> {
    return {
      is_archived: false,
    };
  }
}

/** The endpoint requires `customer` as a top-level param, which `IListParams` has no slot for. */
export interface IGoogleAdsCalloutListParams extends IListParams<IGoogleAdsCallout> {
  customer: string;
}

export class GoogleAdsCallouts extends Collection<GoogleAdsCallout> {
  public getModel(attributes: Partial<IGoogleAdsCallout>): GoogleAdsCallout {
    return new GoogleAdsCallout(attributes);
  }

  public getClassName(): string {
    return 'google_ads_callouts';
  }

  /*
   * Callouts live under the Google Ads integration blueprint, flat rather than nested under
   * a customer segment. The trailing slash is load-bearing: every per-model verb is
   * `url() + id`.
   */
  public override url(): string {
    return `${this.getHostname()}integrations/google-ads/callouts/`;
  }

  /** Narrows the params so callers can't forget the required `customer`. */
  public override list(
    params: IGoogleAdsCalloutListParams,
    options: Partial<IListOptions> = {},
    listId?: string,
  ): ListResponse<GoogleAdsCallout> {
    return super.list(params, options, listId);
  }

  /**
   * The endpoint exposes PATCH but not PUT, so the inherited whole-document save would 405.
   * Routing through `patch` keeps the shared save paths working.
   */
  public override async save(
    id: string,
    patch: Partial<IGoogleAdsCallout> = {},
    fetchOptions: Partial<RequestInit> = {},
  ): Promise<GoogleAdsCallout> {
    const model = this.modelsById.get(id);
    if (!model) {
      throw new Error(`Model with id ${id} does not exist in collection`);
    }

    const attributes = { ...model.toDirtyJS(), ...patch };
    return this.patch(id, attributes, { method: 'PATCH', ...fetchOptions });
  }
}
